import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QRCodeCanvas } from "qrcode.react";
import api, { apiError } from "@/lib/api";
import { shortDate, money } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCall } from "@/context/CallContext";
import { useMeetRoom, roleCaps, ROLE_LABEL } from "@/lib/webrtc";
import { Whiteboard } from "@/components/Whiteboard";
import {
  VideoCamera, VideoCameraSlash, Microphone, MicrophoneSlash, Monitor, PhoneSlash,
  Record, ChatCircle, Users, CalendarBlank, Copy, LinkSimple, X, PaperPlaneRight,
  Pencil, Phone, QrCode, ClosedCaptioning, HandWaving, Smiley,
  Gear, ImageSquare, Play, Broadcast, ArrowsLeftRight,
  SquaresFour, SquareHalf, Student, Presentation, Star, CaretDown, MicrophoneStage, UsersThree,
  Question, ChartBar, CheckCircle, HandPalm, ClipboardText, ChartLineUp, Trash, Eye, UserPlus,
} from "@phosphor-icons/react";

function downloadICS(meeting, { link, ext, pstn, pin }) {
  const dt = meeting.scheduled_at ? new Date(meeting.scheduled_at) : new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const toICS = (d) => `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
  const end = new Date(dt.getTime() + 60 * 60 * 1000);
  const desc = [`Join the meeting: ${link}`, ext && `Dial-in extension (colleagues): ${ext}`, pstn && `Dial-in phone: ${pstn}`, pin && `PIN: ${pin}`].filter(Boolean).join("\\n");
  const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//iYeneh//Meet//EN", "BEGIN:VEVENT",
    `UID:${meeting.room_code}@iyeneh`, `DTSTAMP:${toICS(new Date())}`, `DTSTART:${toICS(dt)}`, `DTEND:${toICS(end)}`,
    `SUMMARY:${(meeting.title || "iYeneh Meeting").replace(/[\r\n]+/g, " ")}`, `DESCRIPTION:${desc}`, `URL:${link}`,
    "END:VEVENT", "END:VCALENDAR"].join("\r\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  a.download = `${(meeting.title || "meeting").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function Meet() {
  const [params] = useSearchParams();
  const room = params.get("room");
  return room ? <MeetRoom code={room} /> : <Lobby />;
}

function Lobby() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [schedTitle, setSchedTitle] = useState("");
  const [when, setWhen] = useState("");
  const [share, setShare] = useState(null);
  const [shareExt, setShareExt] = useState(null);
  const { data: pub } = useQuery({ queryKey: ["public-settings"], queryFn: async () => (await api.get("/settings/public")).data });
  const [mode, setMode] = useState("meet");
  const [pin, setPin] = useState("");
  const { data } = useQuery({ queryKey: ["meetings"], queryFn: async () => (await api.get("/meetings")).data });
  const meetings = data?.meetings || [];
  const { data: sub } = useQuery({ queryKey: ["meet-sub"], queryFn: async () => (await api.get("/meet/subscription")).data });
  const [subBusy, setSubBusy] = useState(false);
  const subscribeMeet = async (plan, cycle) => {
    setSubBusy(true);
    try {
      await api.post("/meet/subscribe", { plan, cycle });
      toast.success("HD Meetings activated — you can host now");
      qc.invalidateQueries({ queryKey: ["meet-sub"] });
      qc.invalidateQueries({ queryKey: ["wallet"] });
    } catch (e) { toast.error(apiError(e)); } finally { setSubBusy(false); }
  };

  const startInstant = async () => {
    try {
      const { data } = await api.post("/meetings", { title: title || "Instant meeting", kind: "instant", mode, pin });
      navigate(`/app/meet?room=${data.meeting.room_code}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const schedule = async () => {
    if (!schedTitle || !when) { toast.error("Add a title and time"); return; }
    try {
      await api.post("/meetings", { title: schedTitle, kind: "scheduled", scheduled_at: when, mode, pin });
      toast.success("Meeting scheduled");
      setSchedTitle(""); setWhen("");
      qc.invalidateQueries({ queryKey: ["meetings"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  const guestLink = (code) => `${window.location.origin}/join?room=${code}`;
  const copyLink = (code) => { navigator.clipboard?.writeText(guestLink(code)); toast.success("Guest link copied"); };
  useEffect(() => {
    if (!share) { setShareExt(null); return; }
    setShareExt(null);
    api.post(`/meet/${share.room_code}/dialin`).then(({ data }) => setShareExt(data.ext)).catch(() => setShareExt(null));
  }, [share]);

  return (
    <div className="space-y-6" data-testid="meet-lobby">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Meetings & Video</h1>
        <p className="mt-2 text-muted-foreground">Real group video with screen share, recording and a live whiteboard. Share a link or QR — guests join in one tap, no account needed.</p>
        <button data-testid="meet-branding-link" onClick={() => navigate("/app/meet/branding")} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-1.5 text-xs font-semibold hover:border-primary"><ImageSquare size={14} weight="duotone" /> Meeting branding</button>
        <button data-testid="meet-broadcast-link" onClick={() => navigate("/app/meet/broadcast")} className="ml-2 mt-3 inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-1.5 text-xs font-semibold hover:border-primary"><Broadcast size={14} weight="duotone" /> Live Broadcasting</button>
        <button data-testid="meet-analytics-link" onClick={() => navigate("/app/meet/analytics")} className="ml-2 mt-3 inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-1.5 text-xs font-semibold hover:border-primary"><ChartLineUp size={14} weight="duotone" /> Analytics</button>
      </div>

      {sub?.entitled && sub?.subscription?.active && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.05] px-4 py-3 text-sm" data-testid="meet-plan-badge">
          <span className="font-semibold text-emerald-600 dark:text-emerald-400">HD Meetings {(sub.subscription.plan || "pro") === "business" ? "Business" : "Pro"} active</span>
          {sub.subscription.renews_at && <span className="text-muted-foreground"> · renews {String(sub.subscription.renews_at).slice(0, 10)}</span>}
          {sub.subscription.auto_renew === false && <span className="text-amber-500"> · auto-renew off</span>}
        </div>
      )}

      {sub && !sub.entitled && (
        <div className="rounded-2xl border border-primary/40 bg-primary/[0.05] p-5" data-testid="meet-paywall">
          <h2 className="font-display text-lg font-semibold">Host HD meetings — choose a plan</h2>
          <p className="mt-1 text-sm text-muted-foreground">Joining is always free. To <b>host</b>, activate a plan (charged from your wallet). Starting at ${(((sub.prices?.meet_pro_monthly_cents) ?? 999) / 100).toFixed(2)}/mo.</p>
          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">HD Meetings Pro</p>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[["monthly", "Monthly", "meet_pro_monthly_cents"], ["quarterly", "Quarterly", "meet_pro_quarterly_cents"], ["semi", "6 months", "meet_pro_semi_cents"], ["annual", "Annual · Best value", "meet_pro_annual_cents"]].map(([cyc, label, k]) => (
              <button key={cyc} data-testid={`meet-plan-pro-${cyc}`} disabled={subBusy} onClick={() => subscribeMeet("pro", cyc)}
                className="rounded-xl border border-border p-3 text-left transition-colors hover:border-primary disabled:opacity-60">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="mt-1 font-display text-lg font-semibold">${(((sub.prices?.[k]) ?? 0) / 100).toFixed(2)}</p>
              </button>
            ))}
          </div>
          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">HD Meetings Business</p>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[["monthly", "Monthly", "meet_biz_monthly_cents"], ["annual", "Annual · Best value", "meet_biz_annual_cents"]].map(([cyc, label, k]) => (
              <button key={cyc} data-testid={`meet-plan-biz-${cyc}`} disabled={subBusy} onClick={() => subscribeMeet("business", cyc)}
                className="rounded-xl border border-border p-3 text-left transition-colors hover:border-primary disabled:opacity-60">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="mt-1 font-display text-lg font-semibold">${(((sub.prices?.[k]) ?? 0) / 100).toFixed(2)}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-4" data-testid="meet-mode-bar">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Meeting type</span>
          {[["meet", "Meet"], ["conference", "Conference"], ["webinar", "Webinar"], ["classroom", "Classroom"], ["private", "Private Room"]].map(([k, label]) => (
            <button key={k} data-testid={`meet-mode-${k}`} onClick={() => setMode(k)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${mode === k ? "bg-primary text-primary-foreground" : "border border-border hover:border-primary"}`}>{label}</button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground">PIN (optional)</span>
            <input data-testid="meet-pin" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              placeholder="e.g. 1234" className="w-24 rounded-lg border border-input bg-card px-2 py-1.5 text-center font-mono-metric text-sm outline-none focus:border-primary" />
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground"><VideoCamera size={22} weight="duotone" /></div>
          <h2 className="font-display text-lg font-semibold">Start an instant meeting</h2>
          <input data-testid="meet-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Meeting title (optional)"
            className="mt-3 w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-primary" />
          <button data-testid="meet-start" onClick={startInstant} className="mt-3 w-full rounded-full bg-primary py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.02]">Start now</button>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-primary"><CalendarBlank size={22} weight="duotone" /></div>
          <h2 className="font-display text-lg font-semibold">Schedule a meeting</h2>
          <input data-testid="meet-sched-title" value={schedTitle} onChange={(e) => setSchedTitle(e.target.value)} placeholder="Meeting title"
            className="mt-3 w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-primary" />
          <input data-testid="meet-sched-when" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
            className="mt-3 w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-primary" />
          <button data-testid="meet-schedule" onClick={schedule} className="mt-3 w-full rounded-full border border-border py-3 font-semibold hover:border-primary">Schedule</button>
        </div>
      </div>

      <div>
        <h3 className="font-display text-lg font-semibold">Your meetings</h3>
        {meetings.length === 0 ? (
          <div className="mt-3 rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No meetings yet.</div>
        ) : (
          <div className="mt-3 space-y-2">
            {meetings.map((m) => {
              const ended = m.status === "ended";
              return (
              <div key={m.room_code} data-testid={`meeting-${m.room_code}`} className={`flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3 ${ended ? "opacity-60" : ""}`}>
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{m.title}</p>
                  <p className="text-xs text-muted-foreground">{m.status} · {shortDate(m.created_at)} · Room {m.room_code}</p>
                </div>
                {ended ? (
                  <span className="rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-muted-foreground">Ended</span>
                ) : (
                  <>
                    <button data-testid={`meet-qr-${m.room_code}`} onClick={() => setShare(m)} className="rounded-full border border-border p-2 hover:border-primary"><QrCode size={16} /></button>
                    <button data-testid={`meet-copy-${m.room_code}`} onClick={() => copyLink(m.room_code)} className="rounded-full border border-border p-2 hover:border-primary"><Copy size={16} /></button>
                    <button data-testid={`meet-join-${m.room_code}`} onClick={() => navigate(`/app/meet?room=${m.room_code}`)}
                      className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Join</button>
                  </>
                )}
              </div>
            ); })}
          </div>
        )}
      </div>

      {share && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShare(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-card p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
            <button onClick={() => setShare(null)} className="ml-auto block text-muted-foreground"><X size={18} /></button>
            <h3 className="font-display text-lg font-semibold">Invite to “{share.title}”</h3>
            <p className="text-xs text-muted-foreground">Scan to join instantly — no account needed.</p>
            <div className="mx-auto mt-4 w-fit rounded-2xl bg-white p-4"><QRCodeCanvas value={guestLink(share.room_code)} size={180} /></div>
            <button onClick={() => copyLink(share.room_code)} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground"><LinkSimple size={16} /> Copy guest link</button>
            <div className="mt-3 space-y-1 rounded-xl border border-border bg-secondary/40 p-3 text-left text-xs">
              <p className="font-semibold text-muted-foreground">Can’t join by video? Dial in:</p>
              {shareExt && <p data-testid="share-dialin-ext"><Phone size={12} className="mr-1 inline" /> Extension <span className="font-mono-metric font-semibold">{shareExt}</span> <span className="text-muted-foreground">(colleagues)</span></p>}
              {pub?.dialin_enabled && pub?.dialin_number && <p data-testid="share-dialin-phone"><Phone size={12} className="mr-1 inline" /> {pub.dialin_number}</p>}
              {share.pin && <p>PIN: <span className="font-mono-metric font-semibold">{share.pin}</span></p>}
              {!shareExt && !(pub?.dialin_enabled && pub?.dialin_number) && <p className="text-muted-foreground">Dial-in available on Business & Enterprise plans.</p>}
            </div>
            <button data-testid="share-ics" onClick={() => downloadICS(share, { link: guestLink(share.room_code), ext: shareExt, pstn: pub?.dialin_enabled ? pub?.dialin_number : "", pin: share.pin })} className="mt-2 flex w-full items-center justify-center gap-2 rounded-full border border-border py-2.5 text-sm font-semibold hover:border-primary"><CalendarBlank size={16} /> Add to calendar</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function MeetRoom({ code, guest = false, guestName = "Guest", pin }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const callCtx = useCall();
  const name = guest ? guestName : (user?.name || "Host");
  const [sp] = useSearchParams();
  const isCall = sp.get("call") === "1";
  const audioOnly = sp.get("audio") === "1";
  const cid = sp.get("cid");
  const [meeting, setMeeting] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [chat, setChat] = useState([{ from: "System", text: "You joined the meeting." }]);
  const [msg, setMsg] = useState("");
  const [showChat, setShowChat] = useState(false);
  const [showWb, setShowWb] = useState(false);
  const [showPeople, setShowPeople] = useState(false);
  const [showReact, setShowReact] = useState(false);
  const [reactions, setReactions] = useState([]);
  const [dialin, setDialin] = useState(null);
  const [captionsAllowed, setCaptionsAllowed] = useState(false);
  const [caption, setCaption] = useState(null);
  const capTimer = useRef(null);
  const wbHandler = useRef(null);
  const timer = useRef(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showXfer, setShowXfer] = useState(false);
  const [showBreakout, setShowBreakout] = useState(false);
  const [showGoLive, setShowGoLive] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showQna, setShowQna] = useState(false);
  const [meetingSettings, setMeetingSettings] = useState({});
  const [globalMeet, setGlobalMeet] = useState({});
  const [videoStage, setVideoStage] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [bgBusy, setBgBusy] = useState(false);
  const [branding, setBranding] = useState(null);
  const [orgBgs, setOrgBgs] = useState([]);
  const [broadcastLive, setBroadcastLive] = useState(false);
  const videoStageRef = useRef(null);
  const meetSettingsRef = useRef({});
  const roomRef = useRef(null);

  const room = useMeetRoom({
    code, name, isHost: !guest, audioOnly, pin,
    mode: meeting?.mode || "meet", roleKey: guest ? undefined : (user?.id || user?._id), authed: !guest,
    onBreakoutAnnounce: (t) => toast(`📢 ${t}`, { duration: 6000 }),
    onBreakoutNotice: (kind, rooms) => {
      if (kind === "denied") toast.error("Breakout recording needs an eligible plan (Enterprise by default).");
      else if (kind === "warn") toast.warning(`No signed-in recorder in: ${(rooms || []).join(", ")} — those rooms won't record.`);
    },
    onChat: (m) => setChat((c) => [...c, m]),
    onWhiteboard: (op) => wbHandler.current?.(op),
    onDenied: (reason) => {
      toast.error(reason === "pin" ? "Incorrect meeting PIN." : reason === "locked" ? "This meeting is locked by the host." : "The host didn't let you in.");
      navigate(guest ? "/" : "/app/meet");
    },
    onCaption: (c) => {
      setCaption(c);
      clearTimeout(capTimer.current);
      capTimer.current = setTimeout(() => setCaption(null), 6000);
    },
    onReaction: (r) => {
      const id = Math.random().toString(36).slice(2);
      setReactions((x) => [...x, { id, ...r }]);
      setTimeout(() => setReactions((x) => x.filter((e) => e.id !== id)), 4000);
    },
    onKicked: () => { toast.error("You were removed from the meeting."); clearInterval(timer.current); navigate(guest ? "/" : "/app/meet"); },
    onForceMuted: () => toast("You were muted by the host."),
    onForceCamOff: () => toast("Your camera was turned off by the host."),
    onVideo: (m) => { if (m.action === "stop") setVideoStage(null); else setVideoStage({ url: m.url, kind: m.kind }); },
    onRoomSettings: (m) => { if (m.settings) setMeetingSettings((s) => ({ ...s, ...m.settings })); },
    onPeerJoined: () => { if (!guest) setTimeout(() => { const vs = videoStageRef.current; if (vs) roomRef.current?.sendVideo({ action: "play", url: vs.url, kind: vs.kind }); roomRef.current?.sendRoomSettings({ settings: meetSettingsRef.current }); }, 900); },
  });

  roomRef.current = room;
  useEffect(() => { videoStageRef.current = videoStage; }, [videoStage]);
  useEffect(() => {
    api.get(`/meetings/${code}/branding`).then(({ data }) => setBranding(data.branding)).catch(() => {});
    if (!guest) api.get("/meet/branding").then(({ data }) => setOrgBgs(data.backgrounds || [])).catch(() => {});
  }, [code, guest]);

  const caps = roleCaps(room.myRole, meeting?.mode || "meet");
  const [view, setView] = useState("gallery");
  const [localPin, setLocalPin] = useState(null);
  useEffect(() => { const md = meeting?.mode; if (!md) return; setView(md === "webinar" ? "webinar" : md === "classroom" ? "classroom" : "gallery"); }, [meeting?.mode]);
  useEffect(() => { if (room.spotlightId) setView("spotlight"); }, [room.spotlightId]);
  const tryMic = () => { if (!room.canSpeakSelf) { toast("Ask the host to let you speak"); return; } room.toggleMic(); };
  const tryCam = () => { if (!room.canSpeakSelf) { toast("Ask the host to let you speak"); return; } room.toggleCam(); };

  const asset = (u) => (!u ? "" : (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`));
  const ytEmbed = (u) => {
    const m = u.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{6,})/);
    return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0` : u;
  };
  const fullscreen = meetingSettings.fullscreen ?? globalMeet.fullscreen ?? false;
  const bgUrl = asset(meetingSettings.background_url || globalMeet.background_url || "");
  useEffect(() => { meetSettingsRef.current = { fullscreen, background_url: meetingSettings.background_url || globalMeet.background_url || "" }; }, [fullscreen, meetingSettings, globalMeet]);

  const applySettings = async (patch) => {
    setMeetingSettings((s) => ({ ...s, ...patch }));
    roomRef.current?.sendRoomSettings({ settings: patch });
    if (!guest) { try { await api.patch(`/meetings/${code}/settings`, patch); } catch { toast.error("Couldn't save that setting"); } }
  };
  const playVideo = (url) => {
    if (!url || !url.trim()) { toast.error("Paste a video link first"); return; }
    const kind = /youtu\.?be/.test(url) ? "youtube" : "file";
    setVideoStage({ url, kind });
    roomRef.current?.sendVideo({ action: "play", url, kind });
    toast.success("Playing for everyone");
  };
  const stopVideo = () => { setVideoStage(null); roomRef.current?.sendVideo({ action: "stop" }); };
  const uploadBg = async (file) => {
    if (!file) return;
    setBgBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/meet/background", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await applySettings({ background_url: data.url });
      toast.success("Background updated");
    } catch (e) { toast.error(apiError(e)); } finally { setBgBusy(false); }
  };

  useEffect(() => {
    const p = (guest || isCall) ? api.get(`/meetings/${code}/public`) : api.get(`/meetings/${code}`);
    p.then(({ data }) => { const mm = data.meeting || data; setMeeting(mm); if (mm.settings) setMeetingSettings(mm.settings); }).catch(() => toast.error("Meeting not found"));
    api.get("/settings/public").then(({ data }) => {
      if (data.dialin_enabled && data.dialin_number) setDialin(data.dialin_number);
      setCaptionsAllowed(!!data.live_captions);
      setGlobalMeet({ fullscreen: data.meet_fullscreen, background_url: data.meet_background_url });
    }).catch(() => {});
    timer.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => { clearInterval(timer.current); clearTimeout(capTimer.current); };
  }, [code, guest]);

  const onCaptions = () => { const ok = room.toggleCaptions(); if (!ok && !room.captionsOn) toast.error("Live captions aren't supported in this browser."); };

  const send = () => { if (!msg.trim()) return; room.sendChat(msg.trim()); setMsg(""); };

  const toggleRecord = () => {
    if (room.recording) room.stopRecording(`Meeting: ${meeting?.title || code}`, code).then((id) => { if (id) { toast.success("Recording saved to vault"); qc.invalidateQueries({ queryKey: ["recordings"] }); } });
    else { const ok = room.startRecording(); toast[ok ? "success" : "error"](ok ? "Recording started" : "No camera/screen to record"); }
  };

  const end = async () => {
    clearInterval(timer.current);
    if (isCall) {
      if (cid) {
        try {
          const { data } = await api.post(`/calls/${cid}/end`, { duration_seconds: elapsed });
          if (data?.cost_cents) toast.success(`Call ended · billed ${money(data.cost_cents)}`);
          else toast.success("Call ended");
        } catch { /* noop */ }
      }
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["call-history"] });
      navigate("/app/call");
      return;
    }
    if (!guest) {
      if (room.recording) { try { await room.stopRecording(`Meeting: ${meeting?.title || code}`, code); qc.invalidateQueries({ queryKey: ["recordings"] }); } catch {} }
      try { await api.post(`/meetings/${code}/end`, { duration_seconds: elapsed, record: false }); } catch {}
      qc.invalidateQueries({ queryKey: ["meetings"] });
    }
    navigate(guest ? "/" : "/app/meet");
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const total = room.participantCount;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#08111F] text-white" data-testid="meet-room">
      {bgUrl && <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bgUrl})` }} data-testid="meet-bg" />}
      {bgUrl && <div className="absolute inset-0 bg-[#08111F]/85" />}
      {callCtx?.groupStatus?.room_code === code && (callCtx.groupStatus.members || []).length > 0 && (
        <div className="absolute left-1/2 top-4 z-[55] w-[min(92vw,360px)] -translate-x-1/2 rounded-2xl border border-white/10 bg-black/60 p-3 backdrop-blur-md" data-testid="group-ring-status">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-white/60">Group call · who's joining</p>
          <div className="space-y-1.5">
            {callCtx.groupStatus.members.map((mm, i) => (
              <div key={i} className="flex items-center justify-between gap-3 text-sm" data-testid={`ring-member-${i}`}>
                <span className="truncate">{mm.name}</span>
                <span className={`flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold ${mm.status === "joined" ? "text-emerald-400" : mm.status === "declined" ? "text-red-400" : "text-amber-300"}`}>
                  <span className={`h-2 w-2 rounded-full ${mm.status === "joined" ? "bg-emerald-400" : mm.status === "declined" ? "bg-red-400" : "bg-amber-300 animate-pulse"}`} />
                  {mm.status === "joined" ? "Joined" : mm.status === "declined" ? "Declined" : "Ringing…"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {caps.admin && showSettings && (
        <div className="absolute inset-0 z-[60] flex justify-end bg-black/50" onClick={() => setShowSettings(false)} data-testid="meet-settings-panel">
          <div className="h-full w-full max-w-sm overflow-y-auto bg-[#0b1020] p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">Meeting settings</h3>
              <button data-testid="settings-close" onClick={() => setShowSettings(false)} className="text-white/50"><X size={18} /></button>
            </div>
            <div className="mt-5">
              <p className="text-sm font-semibold">Waiting room</p>
              <p className="text-xs text-white/50">Guests must be admitted before they can join.</p>
              <button data-testid="settings-lobby" onClick={() => room.setLobby(!room.lobbyOn)} className={`mt-2 rounded-full px-4 py-1.5 text-xs font-semibold ${room.lobbyOn ? "bg-primary text-primary-foreground" : "bg-white/10"}`}>Lobby {room.lobbyOn ? "on" : "off"}</button>
            </div>
            <div className="mt-5">
              <p className="text-sm font-semibold">Lock meeting</p>
              <p className="text-xs text-white/50">When locked, no new participants can join — even with the link or PIN.</p>
              <button data-testid="settings-lock" onClick={() => room.lockRoom(!room.locked)} className={`mt-2 rounded-full px-4 py-1.5 text-xs font-semibold ${room.locked ? "bg-red-500 text-white" : "bg-white/10"}`}>{room.locked ? "Locked" : "Unlocked"}</button>
            </div>
            <div className="mt-5">
              <p className="text-sm font-semibold">Screen layout</p>
              <p className="text-xs text-white/50">Full screen spotlights one person; divided shows everyone in a grid.</p>
              <button data-testid="settings-fullscreen" onClick={() => applySettings({ fullscreen: !fullscreen })} className={`mt-2 rounded-full px-4 py-1.5 text-xs font-semibold ${fullscreen ? "bg-primary text-primary-foreground" : "bg-white/10"}`}>{fullscreen ? "Full screen" : "Divided screen"}</button>
            </div>
            <div className="mt-5">
              <p className="text-sm font-semibold">Meeting background</p>
              <div className="mt-2 flex items-center gap-2">
                <label data-testid="settings-bg-upload" className="flex cursor-pointer items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold"><ImageSquare size={14} /> {bgBusy ? "Uploading…" : "Upload image"}<input type="file" accept="image/*" className="hidden" onChange={(e) => uploadBg(e.target.files?.[0])} /></label>
                {bgUrl && <button data-testid="settings-bg-clear" onClick={() => applySettings({ background_url: "" })} className="rounded-full bg-white/10 px-3 py-1.5 text-xs">Clear</button>}
              </div>
              {orgBgs.length > 0 && (
                <div className="mt-3" data-testid="settings-org-bgs">
                  <p className="mb-1.5 text-xs text-white/50">Your team's backgrounds</p>
                  <div className="grid grid-cols-3 gap-2">
                    {orgBgs.map((b) => (
                      <button key={b.id} data-testid={`org-bg-${b.id}`} onClick={() => applySettings({ background_url: b.url })}
                        className="aspect-video overflow-hidden rounded-lg border border-white/10 hover:border-primary">
                        <img src={asset(b.url)} alt={b.name} className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-5">
              <p className="text-sm font-semibold">My camera background</p>
              <p className="text-xs text-white/50">Blur or replace what's behind you (uses the meeting background image).</p>
              <div className="mt-2 flex gap-2">
                <button data-testid="cam-bg-none" onClick={() => room.setBackground("none")} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${room.bgMode === "none" ? "bg-primary text-primary-foreground" : "bg-white/10"}`}>None</button>
                <button data-testid="cam-bg-blur" onClick={async () => { const ok = await room.setBackground("blur"); if (!ok) toast.error("A camera is needed for backgrounds"); }} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${room.bgMode === "blur" ? "bg-primary text-primary-foreground" : "bg-white/10"}`}>Blur</button>
                <button data-testid="cam-bg-image" onClick={async () => { if (!bgUrl) { toast.error("Upload a background image above first"); return; } const ok = await room.setBackground("image", bgUrl); if (!ok) toast.error("Couldn't start the background"); }} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${room.bgMode === "image" ? "bg-primary text-primary-foreground" : "bg-white/10"}`}>Image</button>
              </div>
            </div>
            <div className="mt-5">
              <p className="text-sm font-semibold">Play a video on screen</p>
              <p className="text-xs text-white/50">Paste a YouTube link or a video URL — everyone sees it.</p>
              <div className="mt-2 flex gap-2">
                <input data-testid="settings-video-url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://youtu.be/…" className="min-w-0 flex-1 rounded-lg bg-white/5 px-3 py-2 text-sm outline-none" />
                <button data-testid="settings-video-play" onClick={() => playVideo(videoUrl)} className="flex items-center rounded-lg bg-primary px-3 text-sm font-semibold"><Play size={16} weight="fill" /></button>
              </div>
              {videoStage && <button data-testid="settings-video-stop" onClick={stopVideo} className="mt-2 rounded-full bg-destructive/80 px-3 py-1.5 text-xs font-semibold">Stop the video</button>}
            </div>
            <div className="mt-5 rounded-xl border border-white/10 p-3">
              <p className="flex items-center gap-2 text-sm font-semibold"><Broadcast size={16} /> Broadcast live (YouTube / Facebook)</p>
              <p className="mt-1 text-xs text-white/50">Save your stream key in Command Center → Meet Settings, then go live with a free encoder like <span className="font-semibold text-white/80">OBS Studio</span>: paste the RTMP URL + key, add this meeting window as a source, and click Start Streaming.</p>
            </div>
          </div>
        </div>
      )}
      {room.waiting && (
        <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-[#08111F] text-center" data-testid="waiting-room">
          <div className="h-14 w-14 animate-pulse rounded-2xl bg-primary" />
          <h2 className="mt-5 font-display text-xl font-semibold">Waiting for the host to let you in…</h2>
          <p className="mt-1 text-sm text-white/50">You'll join {meeting?.title || "the meeting"} as soon as you're admitted.</p>
          <button data-testid="waiting-leave" onClick={end} className="mt-6 rounded-full border border-white/15 px-5 py-2 text-sm text-white/70">Leave</button>
        </div>
      )}

      {caps.manage && room.knocks.length > 0 && (
        <div className="absolute right-3 top-16 z-40 w-64 space-y-2" data-testid="knock-panel">
          {room.knocks.map((k) => (
            <div key={k.id} className="rounded-xl border border-white/10 bg-white/[0.06] p-3 backdrop-blur">
              <p className="text-sm"><span className="font-semibold">{k.name}</span> wants to join</p>
              <div className="mt-2 flex gap-2">
                <button data-testid={`admit-${k.id}`} onClick={() => room.admit(k.id)} className="flex-1 rounded-full bg-primary py-1.5 text-xs font-semibold text-primary-foreground">Admit</button>
                <button data-testid={`deny-${k.id}`} onClick={() => room.deny(k.id)} className="flex-1 rounded-full bg-white/10 py-1.5 text-xs">Deny</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="relative z-40 flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          {branding?.brand_color && <span className="h-8 w-1 rounded-full" style={{ backgroundColor: branding.brand_color }} data-testid="meet-header-accent" />}
          {branding?.logo_url && <img src={asset(branding.logo_url)} alt="" className="h-8 w-auto max-w-[120px] object-contain" data-testid="meet-header-logo" />}
          <div>
            <p className="font-display font-semibold">{meeting?.title || "Meeting"}{branding?.brand_name ? <span className="ml-2 text-xs font-normal text-white/40">· {branding.brand_name}</span> : ""}</p>
            <p className="text-xs text-white/50">Room {code} · {fmt(elapsed)} · {total} in call · {ROLE_LABEL[room.myRole] || "Panelist"}{room.recording ? " · ● Recording" : ""}{room.connected ? "" : " · connecting…"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ViewSwitcher view={view} setView={setView} />
          <button data-testid="meet-close" onClick={end} className="rounded-full border border-white/15 px-3 py-1.5 text-sm text-white/70"><X size={16} /></button>
        </div>
      </div>

      <div className="relative z-0 flex flex-1 gap-3 overflow-hidden px-4 pb-4">
        <Stage room={room} name={name} guest={guest} view={view} localPin={localPin} setLocalPin={setLocalPin}
          caps={caps} videoStage={videoStage} asset={asset} ytEmbed={ytEmbed} stopVideo={stopVideo} />

        {showChat && (
          <div className="flex w-72 flex-col rounded-2xl border border-white/10 bg-white/[0.03]" data-testid="chat-panel">
            <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3 text-sm font-semibold"><ChatCircle size={16} /> Chat <button onClick={() => setShowChat(false)} className="ml-auto text-white/50"><X size={14} /></button></div>
            <div className="flex-1 space-y-2 overflow-y-auto p-3 text-sm">
              {chat.map((c, i) => (<div key={i}><span className="text-white/50">{c.from}: </span>{c.text}</div>))}
            </div>
            <div className="flex gap-2 border-t border-white/10 p-2">
              <input data-testid="chat-input" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Message" className="min-w-0 flex-1 rounded-lg bg-white/5 px-3 py-2 text-sm outline-none" />
              <button data-testid="chat-send" onClick={send} className="rounded-lg bg-primary px-3"><PaperPlaneRight size={16} weight="bold" /></button>
            </div>
          </div>
        )}

        {showPeople && (
          <div className="flex w-72 flex-col rounded-2xl border border-white/10 bg-white/[0.03]" data-testid="people-panel">
            <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3 text-sm font-semibold"><Users size={16} /> People ({total}) <button onClick={() => setShowPeople(false)} className="ml-auto text-white/50"><X size={14} /></button></div>
            {caps.manage && (
              <div className="flex flex-wrap items-center gap-2 border-b border-white/10 px-3 py-2">
                <button data-testid="lobby-toggle" onClick={() => room.setLobby(!room.lobbyOn)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${room.lobbyOn ? "bg-primary text-primary-foreground" : "bg-white/10"}`}>Lobby {room.lobbyOn ? "on" : "off"}</button>
                <button data-testid="mute-all" onClick={room.muteAll} className="rounded-full bg-white/10 px-3 py-1.5 text-xs">Mute all</button>
                {room.spotlightId && <button data-testid="clear-spotlight" onClick={room.clearSpotlight} className="rounded-full bg-white/10 px-3 py-1.5 text-xs">Clear spotlight</button>}
              </div>
            )}
            <div className="flex-1 space-y-1 overflow-y-auto p-2 text-sm">
              {caps.manage && room.remotes.some((r) => r.hand) && (
                <div className="mb-1 rounded-xl border border-amber-500/30 bg-amber-500/10 p-2" data-testid="hand-queue">
                  <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-amber-300"><HandWaving size={13} weight="fill" /> Raised hands ({room.remotes.filter((r) => r.hand).length})</p>
                  <div className="space-y-1">
                    {room.remotes.filter((r) => r.hand).map((r) => (
                      <div key={r.id} className="flex items-center gap-1.5 text-xs" data-testid={`hand-${r.id}`}>
                        <span className="min-w-0 flex-1 truncate">{r.name}</span>
                        {r.canSpeak === false && <button data-testid={`hand-allow-${r.id}`} onClick={() => room.grantSpeak(r.id, true)} className="rounded-md bg-emerald-500/80 px-2 py-1 text-[10px] font-semibold">Let speak</button>}
                        <button data-testid={`hand-panelist-${r.id}`} onClick={() => room.setRole(r.id, "panelist")} className="rounded-md bg-white/10 px-2 py-1 text-[10px]">Panelist</button>
                        <button data-testid={`hand-lower-${r.id}`} onClick={() => room.lowerHand(r.id)} className="rounded-md bg-white/10 px-2 py-1 text-[10px]">Lower</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 rounded-lg px-2 py-2">
                <span className="min-w-0 flex-1 truncate">{name} (You) · {ROLE_LABEL[room.myRole] || "Panelist"}</span>
                {room.handRaised && <HandWaving size={15} weight="fill" className="text-amber-400" />}
                {room.mic ? <Microphone size={15} /> : <MicrophoneSlash size={15} className="text-destructive" />}
              </div>
              {room.remotes.map((r) => (
                <div key={r.id} data-testid={`person-${r.id}`} className="flex flex-wrap items-center gap-1.5 rounded-lg px-2 py-2 hover:bg-white/5">
                  <span className="min-w-[90px] flex-1 truncate">{r.name}</span>
                  {r.hand && <HandWaving size={15} weight="fill" className="text-amber-400" />}
                  {r.mic === false ? <MicrophoneSlash size={15} className="text-destructive" /> : <Microphone size={15} />}
                  {caps.manage && (
                    <>
                      <RoleMenu r={r} room={room} />
                      <button data-testid={`mute-${r.id}`} title="Mute" onClick={() => room.forceMute(r.id)} className="rounded-md bg-white/10 px-2 py-1 text-[10px]">Mute</button>
                      <button data-testid={`remove-${r.id}`} title="Remove" onClick={() => room.removePeer(r.id)} className="rounded-md bg-destructive/80 px-2 py-1 text-[10px]">Remove</button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {showWb && <Whiteboard send={room.sendWhiteboard} onClose={() => setShowWb(false)} onReady={(fn) => { wbHandler.current = fn; }} />}
        {showQna && <InteractivePanel room={room} caps={caps} code={code} mode={meeting?.mode} onClose={() => setShowQna(false)} />}
        {reactions.length > 0 && (
          <div className="pointer-events-none absolute inset-x-0 bottom-4 z-30 flex flex-wrap justify-center gap-2" data-testid="reactions-overlay">
            {reactions.map((r) => (<div key={r.id} className="animate-fade-up rounded-full bg-black/50 px-3 py-1 text-2xl">{r.emoji}</div>))}
          </div>
        )}
      </div>

      {caption && (
        <div className="pointer-events-none px-4 pb-1" data-testid="caption-bar">
          <div className="mx-auto max-w-2xl rounded-xl bg-black/70 px-4 py-2 text-center text-sm"><span className="text-white/50">{caption.from}: </span>{caption.text}</div>
        </div>
      )}
      {dialin && (
        <div className="px-4 pb-1 text-center text-xs text-white/50"><Phone size={12} className="mr-1 inline" /> Dial-in: {dialin} · PIN {code}</div>
      )}

      <div className="relative z-10 flex flex-wrap items-center justify-center gap-3 border-t border-white/10 py-4">
        <Ctrl on={room.mic} onClick={tryMic} On={Microphone} Off={MicrophoneSlash} testid="ctrl-mic" />
        {!audioOnly && <Ctrl on={room.cam} onClick={tryCam} On={VideoCamera} Off={VideoCameraSlash} testid="ctrl-cam" />}
        {!audioOnly && caps.share && <button data-testid="ctrl-share" onClick={room.toggleShare} className={`flex h-12 w-12 items-center justify-center rounded-full ${room.sharing ? "bg-primary" : "bg-white/10"}`}><Monitor size={20} weight="duotone" /></button>}
        {caps.share && <button data-testid="ctrl-whiteboard" onClick={() => setShowWb((v) => !v)} className={`flex h-12 w-12 items-center justify-center rounded-full ${showWb ? "bg-primary" : "bg-white/10"}`}><Pencil size={20} weight="duotone" /></button>}
        <button data-testid="ctrl-chat" onClick={() => setShowChat((v) => !v)} className={`flex h-12 w-12 items-center justify-center rounded-full ${showChat ? "bg-primary" : "bg-white/10"}`}><ChatCircle size={20} weight="duotone" /></button>
        <button data-testid="ctrl-hand" onClick={room.raiseHand} className={`flex h-12 w-12 items-center justify-center rounded-full ${room.handRaised ? "bg-amber-500 text-white" : "bg-white/10"}`}><HandWaving size={20} weight="duotone" /></button>
        {(caps.manage || ["webinar", "classroom", "conference"].includes(meeting?.mode)) && !isCall && (
          <button data-testid="ctrl-qna" onClick={() => setShowQna((v) => !v)} className={`relative flex h-12 w-12 items-center justify-center rounded-full ${showQna ? "bg-primary" : "bg-white/10"}`}>
            <Question size={20} weight="duotone" />
            {room.qna.filter((q) => !q.answered).length > 0 && <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold">{room.qna.filter((q) => !q.answered).length}</span>}
          </button>
        )}
        <div className="relative">
          <button data-testid="ctrl-reactions" onClick={() => setShowReact((v) => !v)} className={`flex h-12 w-12 items-center justify-center rounded-full ${showReact ? "bg-primary" : "bg-white/10"}`}><Smiley size={20} weight="duotone" /></button>
          {showReact && (
            <div className="absolute bottom-14 left-1/2 flex -translate-x-1/2 gap-1 rounded-full bg-[#0b1020] p-2 shadow-xl" data-testid="reaction-bar">
              {["👍", "❤️", "😂", "🎉", "👏", "🙏"].map((e) => (
                <button key={e} data-testid={`reaction-${e}`} onClick={() => { room.sendReaction(e); setShowReact(false); }} className="rounded-full px-1 text-2xl transition-transform hover:scale-125">{e}</button>
              ))}
            </div>
          )}
        </div>
        <button data-testid="ctrl-people" onClick={() => setShowPeople((v) => !v)} className={`relative flex h-12 w-12 items-center justify-center rounded-full ${showPeople ? "bg-primary" : "bg-white/10"}`}><Users size={20} weight="duotone" />{caps.manage && room.knocks.length > 0 && <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold">{room.knocks.length}</span>}</button>
        {captionsAllowed && <button data-testid="ctrl-captions" onClick={onCaptions} className={`flex h-12 w-12 items-center justify-center rounded-full ${room.captionsOn ? "bg-primary" : "bg-white/10"}`}><ClosedCaptioning size={20} weight="duotone" /></button>}
        {caps.admin && <button data-testid="ctrl-record" onClick={toggleRecord} className={`flex h-12 w-12 items-center justify-center rounded-full ${room.recording ? "bg-destructive" : "bg-white/10"}`}><Record size={20} weight={room.recording ? "fill" : "regular"} /></button>}
        {caps.admin && <button data-testid="ctrl-settings" onClick={() => setShowSettings(true)} className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10"><Gear size={20} weight="duotone" /></button>}
        {caps.admin && !isCall && <button data-testid="ctrl-breakout" onClick={() => setShowBreakout(true)} title="Breakout rooms" className={`flex h-12 w-12 items-center justify-center rounded-full ${room.breakoutState?.open ? "bg-primary" : "bg-white/10"}`}><UsersThree size={20} weight="duotone" /></button>}
        {caps.admin && !isCall && <button data-testid="ctrl-golive" onClick={() => setShowGoLive(true)} title="Go live" className={`flex h-12 w-12 items-center justify-center rounded-full ${broadcastLive ? "bg-red-600" : "bg-white/10"}`}><Broadcast size={20} weight="duotone" /></button>}
        {caps.admin && !isCall && <button data-testid="ctrl-invite" onClick={() => setShowInvite(true)} title="Invite / dial-in" className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10"><UserPlus size={20} weight="duotone" /></button>}
        {isCall && !guest && <button data-testid="ctrl-transfer" onClick={() => setShowXfer(true)} title="Transfer call" className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10"><ArrowsLeftRight size={20} weight="duotone" /></button>}
        <button data-testid="ctrl-end" onClick={end} className="flex h-12 items-center gap-2 rounded-full bg-destructive px-6 font-semibold"><PhoneSlash size={20} weight="fill" /> Leave</button>
      </div>
      {showXfer && <TransferModal roomCode={code} cid={cid} onClose={() => setShowXfer(false)} onLeave={() => { setShowXfer(false); end(); }} />}
      {showBreakout && <BreakoutPanel room={room} onClose={() => setShowBreakout(false)} />}
      {showGoLive && <GoLivePanel room={room} code={code} title={meeting?.title} onLiveChange={setBroadcastLive} onClose={() => setShowGoLive(false)} />}
      {showInvite && <InvitePanel code={code} title={meeting?.title} onClose={() => setShowInvite(false)} />}
      {room.breakout && (
        <div className="pointer-events-none absolute left-1/2 top-14 z-40 -translate-x-1/2" data-testid="breakout-banner">
          <div className="flex items-center gap-2 rounded-full bg-primary/90 px-4 py-1.5 text-xs font-semibold text-primary-foreground shadow-lg">
            <UsersThree size={14} weight="fill" /> In {room.breakout.name}
            {room.breakout.record && <span className="ml-1 flex items-center gap-1 rounded-full bg-destructive px-2 py-0.5" data-testid="breakout-recording-badge"><Record size={11} weight="fill" /> Recording</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function TransferModal({ roomCode, cid, onClose, onLeave }) {
  const [ext, setExt] = useState("");
  const [warm, setWarm] = useState(false);
  const doXfer = async (announce) => {
    try {
      const { data } = await api.post("/pbx/transfer", { room_code: roomCode, to_ext: ext, cid, announce });
      if (announce) { setWarm(true); toast.success(`Ringing ${data.target_name}${data.online ? "" : " (offline — notified)"} — introduce the caller, then complete.`); }
      else { toast.success(`Transferred to ${data.target_name}`); onLeave(); }
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4" data-testid="transfer-modal" onClick={onClose}>
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0b1424] p-6 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Transfer call</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X size={18} /></button>
        </div>
        <label className="block text-xs font-semibold text-white/60">Transfer to extension
          <input data-testid="transfer-ext" value={ext} onChange={(e) => setExt(e.target.value.replace(/\D/g, "").slice(0, 5))} autoFocus
            className="mt-1.5 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 font-mono-metric text-base outline-none focus:border-primary" placeholder="00100" />
        </label>
        {!warm ? (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button data-testid="transfer-blind" onClick={() => doXfer(false)} disabled={ext.length !== 5}
              className="rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-40">Blind transfer</button>
            <button data-testid="transfer-warm" onClick={() => doXfer(true)} disabled={ext.length !== 5}
              className="rounded-full border border-white/15 py-2.5 text-sm font-semibold text-white disabled:opacity-40">Warm (announce)</button>
          </div>
        ) : (
          <button data-testid="transfer-complete" onClick={onLeave}
            className="mt-4 w-full rounded-full bg-emerald-500 py-2.5 text-sm font-semibold text-white">Complete transfer &amp; leave</button>
        )}
        <p className="mt-3 text-xs text-white/50">Blind drops you immediately. Warm rings them into this call so you can introduce the caller, then leave.</p>
      </div>
    </div>
  );
}

function InteractivePanel({ room, caps, code, mode, onClose }) {
  const [tab, setTab] = useState("qna");
  const [q, setQ] = useState("");
  const [pQ, setPQ] = useState("");
  const [pOpts, setPOpts] = useState(["", ""]);
  const [isQuiz, setIsQuiz] = useState(mode === "classroom");
  const [correct, setCorrect] = useState(0);
  const [answering, setAnswering] = useState(null);
  const [ans, setAns] = useState("");
  const [attend, setAttend] = useState(null);
  const questions = [...room.qna].sort((a, b) => (a.answered - b.answered) || (b.votes - a.votes));
  const poll = room.poll;
  const showClass = caps.manage;

  const submitQ = () => { if (q.trim()) { room.askQuestion(q.trim()); setQ(""); toast.success("Question sent"); } };
  const submitPoll = () => {
    const opts = pOpts.map((o) => o.trim()).filter(Boolean);
    if (!pQ.trim() || opts.length < 2) { toast.error("Add a question and at least 2 options"); return; }
    if (isQuiz) { room.startQuiz(pQ.trim(), opts, Math.min(correct, opts.length - 1)); toast.success("Quiz launched"); }
    else { room.startPoll(pQ.trim(), opts); toast.success("Poll launched"); }
    setPQ(""); setPOpts(["", ""]); setIsQuiz(false); setCorrect(0);
  };
  const loadAttend = async () => {
    try { const { data } = await api.get(`/meetings/${code}/attendance`); setAttend(data); }
    catch { toast.error("Couldn't load attendance"); }
  };
  useEffect(() => { if (tab === "attend") loadAttend(); }, [tab]); // eslint-disable-line
  const totalVotes = poll ? poll.votes.reduce((a, b) => a + b, 0) : 0;
  const fmtSecs = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

  return (
    <div className="flex w-72 flex-col rounded-2xl border border-white/10 bg-white/[0.03]" data-testid="interactive-panel">
      <div className="flex items-center gap-1 border-b border-white/10 px-2 py-2 text-sm font-semibold">
        <button data-testid="tab-qna" onClick={() => setTab("qna")} className={`flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs ${tab === "qna" ? "bg-primary text-primary-foreground" : "text-white/60"}`}><Question size={13} /> Q&amp;A</button>
        <button data-testid="tab-polls" onClick={() => setTab("polls")} className={`flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs ${tab === "polls" ? "bg-primary text-primary-foreground" : "text-white/60"}`}><ChartBar size={13} /> {mode === "classroom" ? "Quiz" : "Polls"}</button>
        {showClass && <button data-testid="tab-attend" onClick={() => setTab("attend")} className={`flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs ${tab === "attend" ? "bg-primary text-primary-foreground" : "text-white/60"}`}><ClipboardText size={13} /> Roll</button>}
        <button onClick={onClose} className="ml-auto text-white/50"><X size={14} /></button>
      </div>

      {tab === "qna" && (
        <>
          <div className="flex-1 space-y-2 overflow-y-auto p-3 text-sm">
            {questions.length === 0 && <p className="py-6 text-center text-xs text-white/40">No questions yet. Ask the first one!</p>}
            {questions.map((qi) => (
              <div key={qi.id} data-testid={`qna-${qi.id}`} className={`rounded-xl border p-2.5 ${qi.answered ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-white/5"}`}>
                <div className="flex items-start gap-2">
                  <button data-testid={`qna-vote-${qi.id}`} onClick={() => room.voteQuestion(qi.id)} className="flex flex-col items-center rounded-lg bg-white/10 px-2 py-1 text-[10px] leading-tight hover:bg-white/20">▲<span className="font-semibold">{qi.votes}</span></button>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-white/50">{qi.from}</p>
                    <p className="text-sm">{qi.text}</p>
                    {qi.answered && qi.answer && <p className="mt-1 rounded-lg bg-emerald-500/10 px-2 py-1 text-xs"><CheckCircle size={11} weight="fill" className="mr-1 inline text-emerald-400" />{qi.answer}</p>}
                    {caps.manage && (
                      answering === qi.id ? (
                        <div className="mt-1.5 flex gap-1">
                          <input data-testid={`qna-answer-input-${qi.id}`} value={ans} onChange={(e) => setAns(e.target.value)} placeholder="Type an answer…" className="flex-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs outline-none" />
                          <button data-testid={`qna-answer-send-${qi.id}`} onClick={() => { room.answerQuestion(qi.id, ans); setAnswering(null); setAns(""); }} className="rounded-md bg-primary px-2 py-1 text-[10px] font-semibold text-primary-foreground">Reply</button>
                        </div>
                      ) : (
                        <div className="mt-1.5 flex gap-1.5">
                          {!qi.answered && <button data-testid={`qna-mark-${qi.id}`} onClick={() => room.answerQuestion(qi.id, "")} className="rounded-md bg-white/10 px-2 py-0.5 text-[10px]">Mark answered</button>}
                          <button data-testid={`qna-reply-${qi.id}`} onClick={() => { setAnswering(qi.id); setAns(""); }} className="rounded-md bg-white/10 px-2 py-0.5 text-[10px]">Reply</button>
                          <button data-testid={`qna-dismiss-${qi.id}`} onClick={() => room.dismissQuestion(qi.id)} className="rounded-md bg-destructive/80 px-2 py-0.5 text-[10px] font-semibold">Dismiss</button>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 border-t border-white/10 p-3">
            <input data-testid="qna-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitQ()} placeholder="Ask a question…" className="flex-1 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:border-primary" />
            <button data-testid="qna-ask" onClick={submitQ} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Ask</button>
          </div>
        </>
      )}

      {tab === "polls" && (
        <div className="flex-1 space-y-3 overflow-y-auto p-3 text-sm">
          {poll && (
            <div data-testid="poll-active" className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="mb-2 font-semibold">{poll.quiz && <span className="mr-1 rounded bg-primary/30 px-1.5 py-0.5 text-[10px]">QUIZ</span>}{poll.question}{poll.closed && <span className="ml-1 text-xs text-white/40">(closed)</span>}</p>
              <div className="space-y-1.5">
                {poll.options.map((o, i) => {
                  const pct = totalVotes ? Math.round((poll.votes[i] / totalVotes) * 100) : 0;
                  const isCorrect = poll.closed && poll.quiz && poll.correct === i;
                  const mine = room.pollChoice?.[poll.id] === i;
                  return (
                    <button key={i} data-testid={`poll-opt-${i}`} disabled={poll.closed} onClick={() => room.votePoll(poll.id, i)}
                      className={`relative w-full overflow-hidden rounded-lg border px-3 py-2 text-left text-xs disabled:cursor-default ${isCorrect ? "border-emerald-400" : mine ? "border-primary" : "border-white/10"}`}>
                      <div className={`absolute inset-y-0 left-0 ${isCorrect ? "bg-emerald-500/30" : "bg-primary/30"}`} style={{ width: `${pct}%` }} />
                      <span className="relative flex justify-between"><span>{isCorrect && <CheckCircle size={12} weight="fill" className="mr-1 inline text-emerald-400" />}{o}{mine && <span className="ml-1 text-[9px] text-primary">• your pick</span>}</span><span className="font-semibold">{pct}% · {poll.votes[i]}</span></span>
                    </button>
                  );
                })}
              </div>
              {caps.manage && !poll.closed && <button data-testid="poll-close" onClick={() => room.closePoll(poll.id)} className="mt-2 w-full rounded-full bg-white/10 py-1.5 text-xs">{poll.quiz ? "End quiz & reveal answer" : "Close poll"}</button>}
            </div>
          )}
          {caps.manage && room.quizResult && (
            <div data-testid="quiz-result" className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
              <p className="mb-1.5 text-xs font-semibold text-emerald-300">Latest quiz results</p>
              {room.quizResult.leaderboard.map((s, i) => (
                <div key={i} className="flex justify-between text-xs"><span className="truncate">{i + 1}. {s.name}</span><span className="font-semibold">{s.correct}/{s.total}</span></div>
              ))}
              {room.quizResult.leaderboard.length === 0 && <p className="text-[11px] text-white/40">No answers submitted.</p>}
            </div>
          )}
          {caps.manage && (
            <div className="rounded-xl border border-dashed border-white/15 p-3">
              <p className="mb-2 text-xs font-semibold text-white/60">Launch a {isQuiz ? "graded quiz" : "poll"}</p>
              <input data-testid="poll-question" value={pQ} onChange={(e) => setPQ(e.target.value)} placeholder="Question" className="mb-2 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:border-primary" />
              {pOpts.map((o, i) => (
                <div key={i} className="mb-1.5 flex items-center gap-1.5">
                  {isQuiz && <input type="radio" name="correct" data-testid={`poll-correct-${i}`} checked={correct === i} onChange={() => setCorrect(i)} title="Correct answer" className="h-4 w-4 accent-emerald-500" />}
                  <input data-testid={`poll-option-${i}`} value={o} onChange={(e) => setPOpts((x) => x.map((v, j) => (j === i ? e.target.value : v)))} placeholder={`Option ${i + 1}`} className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm outline-none focus:border-primary" />
                </div>
              ))}
              <label className="mb-2 mt-1 flex items-center gap-2 text-xs">
                <input type="checkbox" data-testid="quiz-toggle" checked={isQuiz} onChange={(e) => setIsQuiz(e.target.checked)} className="h-4 w-4 accent-[var(--primary)]" />
                Graded quiz (pick the correct answer)
              </label>
              <div className="flex gap-2">
                {pOpts.length < 6 && <button data-testid="poll-add-option" onClick={() => setPOpts((x) => [...x, ""])} className="rounded-full bg-white/10 px-3 py-1 text-xs">+ Option</button>}
                <button data-testid="poll-launch" onClick={submitPoll} className="flex-1 rounded-full bg-primary py-1.5 text-xs font-semibold text-primary-foreground">Launch</button>
              </div>
            </div>
          )}
          {!poll && !caps.manage && <p className="py-6 text-center text-xs text-white/40">No active poll.</p>}
        </div>
      )}

      {tab === "attend" && (
        <div className="flex-1 space-y-2 overflow-y-auto p-3 text-sm" data-testid="attendance-list">
          <div className="flex items-center justify-between">
            <p className="text-xs text-white/60">{attend ? `${attend.present} present · ${attend.total} total` : "Loading…"}</p>
            <button data-testid="attend-refresh" onClick={loadAttend} className="rounded-full bg-white/10 px-3 py-1 text-[11px]">Refresh</button>
          </div>
          {attend?.attendance?.map((a) => (
            <div key={a.role_key} data-testid={`attend-${a.role_key}`} className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2 text-xs">
              <span className="flex items-center gap-1.5 truncate">{a.present && <span className="h-2 w-2 rounded-full bg-emerald-400" />}{a.name}</span>
              <span className="text-white/50">{fmtSecs(a.seconds)}{a.sessions > 1 ? ` · ${a.sessions}×` : ""}</span>
            </div>
          ))}
          {attend && attend.attendance.length === 0 && <p className="py-6 text-center text-xs text-white/40">No attendance recorded yet.</p>}
        </div>
      )}
    </div>
  );
}

function GoLivePanel({ room, code, title, onLiveChange, onClose }) {
  const [dests, setDests] = useState(null);
  const [sel, setSel] = useState([]);
  const [mode, setMode] = useState("live");
  const [session, setSession] = useState(null);
  const [targets, setTargets] = useState([]);
  const [watchUrl, setWatchUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [premieres, setPremieres] = useState([]);
  const [when, setWhen] = useState("");
  const loadPremieres = () => api.get(`/broadcast/premieres`, { params: { room: code } }).then(({ data }) => setPremieres(data.premieres || [])).catch(() => {});

  const refresh = async () => {
    try {
      const { data } = await api.get(`/broadcast/${code}/status`);
      setSession(data.session);
      onLiveChange?.(!!data.session);
      if (data.session) {
        if (data.session.watch_code) setWatchUrl(`${window.location.origin}/watch/${data.session.watch_code}`);
        if (data.session.destinations) {
          setTargets((prev) => (prev.length ? prev : data.session.destinations.map((d) => ({ id: d.id, name: d.name, rtmp_url: d.rtmp_url, stream_key: d.key_masked }))));
        }
      } else { setWatchUrl(""); setTargets([]); }
    } catch { /* ignore */ }
  };
  useEffect(() => {
    api.get("/broadcast/destinations").then(({ data }) => setDests(data.destinations || [])).catch(() => {});
    refresh();
    loadPremieres();
    const iv = setInterval(refresh, 6000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const readyDests = (dests || []).filter((d) => d.has_key);
  const start = async (ids = sel, modeArg = mode, premiereId = null) => {
    if (modeArg !== "record" && ids.length === 0) { toast.error("Select at least one destination or choose Record only"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/broadcast/${code}/start`, { destination_ids: ids, mode: modeArg, premiere_id: premiereId });
      setSession(data.session); setTargets(data.targets || []);
      setWatchUrl(`${window.location.origin}${data.watch_url}`);
      onLiveChange?.(true);
      loadPremieres();
      if (data.record) { const ok = room.startRecording(); if (!ok) toast.error("Couldn't start recording (no camera/mic)"); }
      toast.success(ids.length > 1 ? `You're live on ${ids.length} platforms` : "You're live");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const schedule = async () => {
    if (!when) { toast.error("Pick a date & time"); return; }
    const iso = new Date(when).toISOString();
    if (new Date(iso) <= new Date()) { toast.error("Pick a future time"); return; }
    if (mode !== "record" && sel.length === 0) { toast.error("Select at least one destination for the premiere"); return; }
    setBusy(true);
    try {
      await api.post(`/broadcast/${code}/schedule`, { scheduled_at: iso, destination_ids: sel, mode, title });
      toast.success("Premiere scheduled — share the watch link");
      setWhen("");
      await loadPremieres();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const startPremiere = (p) => start(p.destination_ids || [], p.mode, p.id);
  const cancelPremiere = async (id) => {
    try { await api.delete(`/broadcast/premiere/${id}`); await loadPremieres(); toast.success("Premiere cancelled"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const copyWatch = (wc) => { navigator.clipboard?.writeText(`${window.location.origin}/watch/${wc}`); toast.success("Watch link copied"); };
  const startAll = () => {
    if (readyDests.length === 0) { toast.error("Connect a platform first in Live Broadcasting"); return; }
    const ids = readyDests.map((d) => d.id);
    setSel(ids);
    const m = mode === "record" ? "live" : mode;
    setMode(m);
    start(ids, m);
  };
  const stop = async () => {
    setBusy(true);
    try {
      if (session && session.mode !== "live" && room.recording) {
        try { const rid = await room.stopRecording(session.title || title || "Broadcast", code); if (rid && session.id) await api.patch(`/broadcast/session/${session.id}`, { recording_id: rid }); } catch { /* ignore */ }
      }
      await api.post(`/broadcast/${code}/stop`);
      setSession(null); setTargets([]); onLiveChange?.(false);
      toast.success("Broadcast stopped");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  const embed = watchUrl ? `<iframe src="${watchUrl}" width="640" height="360" allow="autoplay; fullscreen" frameborder="0"></iframe>` : "";

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4" data-testid="golive-panel" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-[#0b1424] p-6 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-lg font-semibold"><Broadcast size={20} weight="duotone" /> Live Broadcasting</h3>
          <button data-testid="golive-close" onClick={onClose} className="text-white/60 hover:text-white"><X size={18} /></button>
        </div>

        {session ? (
          <div className="space-y-4" data-testid="golive-active">
            <div className="flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm">
              <span className="flex items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-1 text-xs font-semibold" data-testid="golive-status"><Broadcast size={13} weight="fill" /> LIVE · {session.mode.replace("_", " + ")}</span>
              {room.recording && <span className="text-xs text-white/60">● recording</span>}
            </div>
            {targets.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-semibold text-white/60">Push to these from your encoder (OBS): add each as a Stream output</p>
                <div className="space-y-1.5">
                  {targets.map((t) => (
                    <div key={t.id} data-testid={`golive-target-${t.id}`} className="rounded-lg border border-white/10 bg-white/5 p-2 text-xs">
                      <p className="font-semibold">{t.name}</p>
                      <div className="mt-1 flex items-center gap-1"><span className="min-w-0 flex-1 truncate font-mono-metric text-white/60">{t.rtmp_url}</span><button onClick={() => copy(t.rtmp_url)} className="rounded bg-white/10 px-2 py-0.5">URL</button><button onClick={() => copy(t.stream_key)} className="rounded bg-white/10 px-2 py-0.5">Key</button></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {watchUrl && (
              <div className="rounded-xl border border-white/10 p-3">
                <p className="text-xs font-semibold text-white/60">Public watch page</p>
                <div className="mt-1 flex items-center gap-1"><input data-testid="golive-watch-link" readOnly value={watchUrl} className="min-w-0 flex-1 rounded bg-white/5 px-2 py-1 text-xs" /><button data-testid="golive-copy-watch" onClick={() => copy(watchUrl)} className="rounded bg-white/10 px-2 py-1 text-xs"><Copy size={13} /></button></div>
                <button data-testid="golive-copy-embed" onClick={() => copy(embed)} className="mt-2 rounded-full bg-white/10 px-3 py-1 text-[11px]">Copy website embed code</button>
              </div>
            )}
            <div className="rounded-xl border border-white/10 p-3" data-testid="golive-viewers">
              <div className="flex items-center justify-between text-xs font-semibold text-white/60">
                <span className="flex items-center gap-1.5"><Eye size={14} weight="fill" /> {room.viewerCount} watching</span>
                <button data-testid="golive-chat-lock" onClick={() => room.lockWatchChat(!room.chatLocked)} className="rounded-full bg-white/10 px-2.5 py-1">{room.chatLocked ? "Resume chat" : "Pause chat"}</button>
              </div>
              <div className="mt-2 max-h-40 space-y-1 overflow-y-auto text-xs" data-testid="golive-viewer-chat">
                {(!room.watchChat || room.watchChat.length === 0) && <p className="text-white/30">No viewer messages yet.</p>}
                {(room.watchChat || []).map((w) => (
                  <div key={w.id} className="flex items-start gap-1">
                    <span className="font-semibold text-white/80">{w.name}:</span>
                    <span className="flex-1 text-white/70">{w.text}</span>
                    <button data-testid={`golive-chat-del-${w.id}`} onClick={() => room.deleteWatchChat(w.id)} className="text-white/30 hover:text-destructive"><Trash size={12} /></button>
                  </div>
                ))}
              </div>
            </div>
            <button data-testid="golive-stop" disabled={busy} onClick={stop} className="w-full rounded-full bg-destructive py-2.5 text-sm font-semibold disabled:opacity-60">Stop broadcasting</button>
          </div>
        ) : (
          <div className="space-y-4">
            {readyDests.length > 0 && (
              <div className="rounded-xl border border-red-500/40 bg-gradient-to-r from-red-600/20 to-fuchsia-600/15 p-3" data-testid="golive-all-card">
                <button data-testid="golive-all" disabled={busy} onClick={startAll}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-600 to-fuchsia-600 py-3 text-sm font-bold text-white shadow-lg transition-transform hover:scale-[1.01] disabled:opacity-60">
                  <Broadcast size={18} weight="fill" /> Go live to all {readyDests.length} platform{readyDests.length > 1 ? "s" : ""} at once
                </button>
                <p className="mt-1.5 text-center text-[11px] text-white/50">One tap simulcasts to {readyDests.map((d) => d.name).join(", ")}.</p>
              </div>
            )}
            <div>
              <p className="mb-1.5 text-xs font-semibold text-white/60">Mode</p>
              <div className="flex gap-2">
                {[["live", "Live only"], ["record", "Record only"], ["live_record", "Live + Record"]].map(([k, l]) => (
                  <button key={k} data-testid={`golive-mode-${k}`} onClick={() => setMode(k)} className={`flex-1 rounded-lg border px-2 py-2 text-xs font-semibold ${mode === k ? "border-primary bg-primary/20" : "border-white/10"}`}>{l}</button>
                ))}
              </div>
            </div>
            {mode !== "record" && (
              <div>
                <p className="mb-1.5 flex items-center justify-between text-xs font-semibold text-white/60">Destinations <a href="/app/meet/broadcast" target="_blank" rel="noreferrer" className="text-primary">Manage</a></p>
                {dests === null ? <p className="rounded-lg border border-dashed border-white/15 p-4 text-center text-xs text-white/40">Loading destinations…</p> : dests.length === 0 ? <p className="rounded-lg border border-dashed border-white/15 p-4 text-center text-xs text-white/40">No destinations yet. Add them in Live Broadcasting.</p> : (
                  <div className="space-y-1.5">
                    {dests.map((d) => (
                      <label key={d.id} data-testid={`golive-dest-${d.id}`} className="flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-sm">
                        <input type="checkbox" checked={sel.includes(d.id)} onChange={() => toggle(d.id)} className="h-4 w-4 accent-[var(--primary)]" />
                        <span className="flex-1 truncate">{d.name} <span className="ml-1 rounded bg-white/10 px-1.5 py-0.5 text-[10px] uppercase">{d.platform}</span></span>
                        {!d.has_key && <span className="text-[10px] text-amber-400">no key</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            <button data-testid="golive-start" disabled={busy} onClick={() => start()} className="w-full rounded-full bg-red-600 py-2.5 text-sm font-semibold disabled:opacity-60">Go live</button>
            <p className="text-[11px] text-white/40">Live push uses your encoder (e.g. OBS) with the destinations above. Record saves to your Recording Vault. Viewers can watch on your public page — no account needed.</p>

            <div className="rounded-xl border border-white/10 p-3" data-testid="golive-schedule">
              <p className="mb-1.5 text-xs font-semibold text-white/60">Schedule a premiere</p>
              <div className="flex flex-wrap items-center gap-2">
                <input data-testid="premiere-when" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none [color-scheme:dark]" />
                <button data-testid="premiere-schedule" disabled={busy} onClick={schedule}
                  className="rounded-full bg-white/10 px-4 py-2 text-xs font-semibold hover:bg-white/20 disabled:opacity-60">Schedule for later</button>
              </div>
              <p className="mt-1 text-[11px] text-white/40">Uses the mode &amp; destinations selected above. Viewers get a countdown page until showtime.</p>
            </div>

            {premieres.length > 0 && (
              <div className="rounded-xl border border-white/10 p-3" data-testid="premiere-list">
                <p className="mb-1.5 text-xs font-semibold text-white/60">Upcoming premieres</p>
                <div className="space-y-1.5">
                  {premieres.map((p) => (
                    <div key={p.id} data-testid={`premiere-${p.id}`} className="flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs">
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold">{p.title}</p>
                        <p className="text-white/40">{new Date(p.scheduled_at).toLocaleString()} · {p.registrations || 0} RSVP{p.status === "ready" && <span className="ml-1 rounded bg-emerald-600/30 px-1.5 py-0.5 text-emerald-300">ready</span>}</p>
                      </div>
                      <button data-testid={`premiere-start-${p.id}`} onClick={() => startPremiere(p)} className="rounded-full bg-red-600 px-3 py-1 font-semibold">Start now</button>
                      <button data-testid={`premiere-copy-${p.id}`} onClick={() => copyWatch(p.watch_code)} className="rounded-full bg-white/10 px-2 py-1"><Copy size={12} /></button>
                      <button data-testid={`premiere-cancel-${p.id}`} onClick={() => cancelPremiere(p.id)} className="rounded-full bg-white/10 px-2 py-1 text-white/50 hover:text-destructive"><Trash size={12} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function InvitePanel({ code, title, onClose }) {
  const [ext, setExt] = useState(null);
  const [extErr, setExtErr] = useState("");
  const [ring, setRing] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.post(`/meet/${code}/dialin`).then(({ data }) => setExt(data.ext)).catch((e) => setExtErr(apiError(e)));
  }, [code]);
  const guestLink = `${window.location.origin}/join?room=${code}`;
  const ringExt = async () => {
    if (!/^\d{5}$/.test(ring.trim())) { toast.error("Enter a 5-digit extension"); return; }
    setBusy(true);
    try { const { data } = await api.post("/pbx/meet-ring", { room_code: code, to_ext: ring.trim() }); toast.success(`Rang ${data.target_name || "ext " + ring} ${data.online ? "" : "(offline — they'll get a notification)"}`); setRing(""); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const textPhone = async () => {
    if (!phone.trim()) { toast.error("Enter a phone number"); return; }
    setBusy(true);
    try { const { data } = await api.post(`/meet/${code}/invite-phone`, { phone: phone.trim() }); toast.success(`Invite texted to ${data.to}`); setPhone(""); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4" data-testid="invite-panel" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl border border-white/10 bg-[#0b1424] p-6 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-lg font-semibold"><UserPlus size={20} weight="duotone" /> Invite to the room</h3>
          <button data-testid="invite-close" onClick={onClose} className="text-white/60 hover:text-white"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div className="rounded-xl border border-white/10 p-3">
            <p className="text-xs font-semibold text-white/60">Dial-in extension</p>
            {ext ? (
              <p className="mt-1 font-display text-2xl font-semibold tracking-widest" data-testid="invite-dialin-ext">{ext}</p>
            ) : extErr ? (
              <p className="mt-1 text-xs text-amber-400" data-testid="invite-dialin-err">{extErr}</p>
            ) : <p className="mt-1 text-xs text-white/40">Allocating…</p>}
            <p className="mt-1 text-[11px] text-white/40">Colleagues can dial this from the iYeneh dialer to jump straight in.</p>
          </div>
          <div className="rounded-xl border border-white/10 p-3">
            <p className="mb-1.5 text-xs font-semibold text-white/60">Ring an extension into this meeting</p>
            <div className="flex gap-2">
              <input data-testid="invite-ring-ext" value={ring} onChange={(e) => setRing(e.target.value)} placeholder="e.g. 00100" maxLength={5}
                className="min-w-0 flex-1 rounded-lg bg-white/5 px-3 py-2 font-mono-metric text-sm outline-none focus:bg-white/10" />
              <button data-testid="invite-ring-go" disabled={busy} onClick={ringExt} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Ring</button>
            </div>
          </div>
          <div className="rounded-xl border border-white/10 p-3">
            <p className="mb-1.5 text-xs font-semibold text-white/60">Text a phone a join link</p>
            <div className="flex gap-2">
              <input data-testid="invite-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 202 555 1234"
                className="min-w-0 flex-1 rounded-lg bg-white/5 px-3 py-2 font-mono-metric text-sm outline-none focus:bg-white/10" />
              <button data-testid="invite-phone-go" disabled={busy} onClick={textPhone} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Text</button>
            </div>
            <p className="mt-1 text-[11px] text-white/40">Turns a phone call into a video room — they tap the link to join.</p>
          </div>
          <div className="flex items-center gap-1 rounded-xl border border-white/10 p-3">
            <input readOnly value={guestLink} className="min-w-0 flex-1 rounded bg-white/5 px-2 py-1 text-xs" data-testid="invite-link" />
            <button data-testid="invite-copy" onClick={() => { navigator.clipboard?.writeText(guestLink); toast.success("Link copied"); }} className="rounded bg-white/10 px-2 py-1 text-xs"><Copy size={13} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BreakoutPanel({ room, onClose }) {
  const st = room.breakoutState || { open: false, record: false, rooms: [], main: [] };
  const [count, setCount] = useState(2);
  const [record, setRecord] = useState(!!st.record);
  const [ann, setAnn] = useState("");
  const hasRooms = st.rooms.length > 0;

  const create = () => {
    const n = Math.max(1, Math.min(20, Number(count) || 1));
    const rooms = Array.from({ length: n }, (_, i) => ({ id: `b${i + 1}`, name: `Room ${i + 1}` }));
    room.createBreakouts(rooms, record);
    toast.success(`${n} breakout room${n > 1 ? "s" : ""} ready — assign people, then open`);
  };
  const roomOptions = [{ id: "main", name: "Main room" }, ...st.rooms.map((r) => ({ id: r.id, name: r.name }))];
  const members = [
    ...st.main.map((m) => ({ ...m, room: "main" })),
    ...st.rooms.flatMap((r) => r.members.map((m) => ({ ...m, room: r.id }))),
  ];

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4" data-testid="breakout-modal" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-[#0b1424] p-6 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Breakout rooms</h3>
          <button data-testid="breakout-close-btn" onClick={onClose} className="text-white/60 hover:text-white"><X size={18} /></button>
        </div>

        {!hasRooms ? (
          <div className="space-y-4">
            <label className="block text-xs font-semibold text-white/60">How many rooms?
              <input data-testid="breakout-count" type="number" min={1} max={20} value={count}
                onChange={(e) => setCount(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-base outline-none focus:border-primary" />
            </label>
            <label className="flex items-center gap-2 text-sm" data-testid="breakout-record-toggle">
              <input type="checkbox" checked={record} onChange={(e) => setRecord(e.target.checked)} className="h-4 w-4 accent-[var(--primary)]" />
              Record each breakout separately
            </label>
            <p className="text-xs text-white/40">Recording is an Enterprise feature by default. Each room is recorded by its first signed-in participant and saved to your Recording Vault under this meeting.</p>
            <button data-testid="breakout-create" onClick={create} className="w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground">Create rooms</button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs">
              <span>{st.rooms.length} rooms · {st.record ? "recording on" : "recording off"}</span>
              <span className={`font-semibold ${st.open ? "text-emerald-400" : "text-white/50"}`}>{st.open ? "● Open" : "Closed"}</span>
            </div>
            <label className={`flex items-center gap-2 text-sm ${st.open ? "opacity-50" : ""}`} data-testid="breakout-record-toggle-live">
              <input type="checkbox" checked={st.record} disabled={st.open}
                onChange={(e) => room.createBreakouts(st.rooms.map((r) => ({ id: r.id, name: r.name })), e.target.checked)}
                className="h-4 w-4 accent-[var(--primary)]" />
              Record each breakout separately {st.open && "(locked while open)"}
            </label>

            {roomOptions.map((ro) => (
              <div key={ro.id} className="rounded-xl border border-white/10 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-semibold">{ro.name}</p>
                  {ro.id !== "main" && <button data-testid={`breakout-join-${ro.id}`} onClick={() => room.joinBreakout(ro.id)} className="rounded-full bg-white/10 px-3 py-1 text-[11px]">Join</button>}
                  {ro.id === "main" && <button data-testid="breakout-join-main" onClick={() => room.joinBreakout("main")} className="rounded-full bg-white/10 px-3 py-1 text-[11px]">Return here</button>}
                </div>
                <div className="space-y-1.5">
                  {members.filter((m) => m.room === ro.id).map((m) => (
                    <div key={m.id} className="flex items-center gap-2 text-xs">
                      <span className="min-w-0 flex-1 truncate">{m.name}</span>
                      <select data-testid={`breakout-assign-${m.id}`} value={m.room}
                        onChange={(e) => room.assignBreakout(m.id, e.target.value)}
                        className="rounded-md border border-white/10 bg-[#0b1424] px-2 py-1 text-[11px] outline-none">
                        {roomOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                      </select>
                    </div>
                  ))}
                  {members.filter((m) => m.room === ro.id).length === 0 && <p className="text-[11px] text-white/30">No one here yet</p>}
                </div>
              </div>
            ))}

            <div className="flex gap-2">
              {!st.open
                ? <button data-testid="breakout-open" onClick={room.openBreakouts} className="flex-1 rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground">Open rooms</button>
                : <button data-testid="breakout-close" onClick={room.closeBreakouts} className="flex-1 rounded-full bg-destructive py-2.5 text-sm font-semibold">Close all &amp; bring everyone back</button>}
            </div>

            {st.open && (
              <div className="flex gap-2">
                <input data-testid="breakout-msg" value={ann} onChange={(e) => setAnn(e.target.value)} placeholder="Broadcast a message to all rooms…"
                  className="flex-1 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:border-primary" />
                <button data-testid="breakout-send" onClick={() => { if (ann.trim()) { room.breakoutMessage(ann.trim()); setAnn(""); toast.success("Broadcast sent"); } }} className="rounded-full bg-white/10 px-4 py-2 text-sm font-semibold">Send</button>
              </div>
            )}
            <button data-testid="breakout-recreate" onClick={() => room.createBreakouts([], false)} className="w-full rounded-full border border-white/10 py-2 text-xs text-white/60">Clear rooms &amp; start over</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ self, name, refEl, stream, muted, mic, cam, sharing, hand, role, onClick, spotlighted, label, big }) {
  const localRef = useRef(null);
  useEffect(() => { if (!self && localRef.current && stream) localRef.current.srcObject = stream; }, [stream, self]);
  const showMuted = self ? !mic : mic === false;
  const remoteHasVideo = !self && stream && stream.getVideoTracks && stream.getVideoTracks().some((t) => t.readyState === "live");
  const roleBadge = role && role !== "panelist" ? (ROLE_LABEL[role] || role) : null;
  return (
    <div onClick={onClick} className={`group relative flex h-full min-h-[110px] cursor-pointer items-center justify-center overflow-hidden rounded-2xl border bg-white/[0.03] ${spotlighted ? "border-primary ring-2 ring-primary" : "border-white/10"}`}>
      <video data-testid={self ? "self-video" : "remote-video"} ref={self ? refEl : localRef} autoPlay playsInline muted={!!muted} className="h-full w-full object-cover" />
      {self && !cam && !sharing && <div className="absolute inset-0 flex items-center justify-center bg-[#0b0d13]"><div className={`flex items-center justify-center rounded-full bg-primary font-semibold ${big ? "h-24 w-24 text-4xl" : "h-16 w-16 text-2xl"}`}>{(name || "Y").charAt(0)}</div></div>}
      {!self && !remoteHasVideo && <div className="absolute inset-0 flex items-center justify-center bg-[#0b0d13]"><div className={`flex items-center justify-center rounded-full bg-primary font-semibold ${big ? "h-24 w-24 text-4xl" : "h-16 w-16 text-2xl"}`}>{(name || "?").charAt(0).toUpperCase()}</div></div>}
      {(label || roleBadge) && <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold">{label || roleBadge}</span>}
      {spotlighted && <Star size={14} weight="fill" className="absolute right-2 top-2 text-primary" />}
      {hand && <span className="absolute right-2 top-8 rounded-full bg-amber-500 px-2 py-1 text-xs" title="Raised hand">✋</span>}
      <span className="absolute bottom-2 left-2 rounded-full bg-black/50 px-2 py-0.5 text-xs">{name}{showMuted ? " (muted)" : ""}{self && sharing ? " · sharing" : ""}</span>
    </div>
  );
}

function TileFor({ t, big, onClick, spotlighted, label }) {
  return <Tile self={t.self} name={t.name} refEl={t.refEl} stream={t.stream} muted={t.muted}
    mic={t.mic} cam={t.cam} sharing={t.sharing} hand={t.hand} role={t.role}
    onClick={onClick} spotlighted={spotlighted} label={label} big={big} />;
}

const STAGE_ROLES = ["host", "cohost", "presenter", "panelist"];
const gridCols = (n) => (n <= 1 ? "grid-cols-1" : n <= 4 ? "grid-cols-1 sm:grid-cols-2" : n <= 9 ? "grid-cols-2 lg:grid-cols-3" : "grid-cols-3 lg:grid-cols-4");

function Stage({ room, name, guest, view, localPin, setLocalPin, caps, videoStage, asset, ytEmbed, stopVideo }) {
  const selfTile = { id: room.selfId, name: `${name} (You)`, self: true, role: room.myRole, mic: room.mic, cam: room.cam, sharing: room.sharing, hand: room.handRaised, refEl: room.localVideoRef, muted: true };
  const remoteTiles = room.remotes.map((r) => ({ id: r.id, name: r.name, stream: r.stream, role: r.role || "panelist", mic: r.mic, hand: r.hand }));
  const everyone = [selfTile, ...remoteTiles];
  const onStageTiles = everyone.filter((t) => STAGE_ROLES.includes(t.role || "panelist"));
  const attendeeCount = everyone.length - onStageTiles.length;
  const list = view === "webinar" ? onStageTiles : everyone;
  const focusId = room.spotlightId || localPin || room.activeSpeaker || remoteTiles[0]?.id || selfTile.id;
  const focused = list.find((t) => t.id === focusId) || list[0] || selfTile;
  const pin = (id) => setLocalPin((p) => (p === id ? null : id));
  const strip = (tiles) => (
    <div className="flex gap-2 overflow-x-auto pb-1" data-testid="stage-strip">
      {tiles.map((t) => <div key={t.id} className="h-24 w-40 shrink-0"><TileFor t={t} onClick={() => pin(t.id)} spotlighted={room.spotlightId === t.id} /></div>)}
    </div>
  );

  let body;
  if (view === "gallery" || view === "webinar") {
    body = (
      <div className={`grid h-full gap-3 ${gridCols(list.length)} auto-rows-fr`} data-testid={`layout-${view}`}>
        {list.map((t) => <TileFor key={t.id} t={t} onClick={() => pin(t.id)} spotlighted={room.spotlightId === t.id} />)}
      </div>
    );
  } else if (view === "sidebyside") {
    const others = list.filter((t) => t.id !== focused.id);
    const second = others[0];
    body = (
      <div className="flex h-full flex-col gap-2" data-testid="layout-sidebyside">
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
          <TileFor t={focused} big onClick={() => pin(focused.id)} spotlighted={room.spotlightId === focused.id} />
          {second ? <TileFor t={second} big onClick={() => pin(second.id)} /> : <div className="rounded-2xl border border-white/10 bg-white/[0.03]" />}
        </div>
        {others.length > 1 && strip(others.slice(1))}
      </div>
    );
  } else if (view === "classroom") {
    const teacher = list.find((t) => ["host", "cohost", "presenter"].includes(t.role)) || focused;
    const students = list.filter((t) => t.id !== teacher.id);
    body = (
      <div className="flex h-full flex-col gap-2" data-testid="layout-classroom">
        <div className="relative min-h-0 flex-1"><TileFor t={teacher} big onClick={() => pin(teacher.id)} label="Teacher" spotlighted={room.spotlightId === teacher.id} /></div>
        {students.length > 0 && strip(students)}
      </div>
    );
  } else {
    const big = view === "presentation" && selfTile.sharing ? selfTile : focused;
    const rest = list.filter((t) => t.id !== big.id);
    body = (
      <div className="flex h-full flex-col gap-2" data-testid={`layout-${view}`}>
        <div className="relative min-h-0 flex-1"><TileFor t={big} big onClick={() => pin(big.id)} spotlighted={room.spotlightId === big.id} /></div>
        {rest.length > 0 && strip(rest)}
      </div>
    );
  }

  return (
    <div className="relative flex-1">
      {videoStage && (
        <div className="absolute inset-0 z-20 flex items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-black" data-testid="video-stage">
          {videoStage.kind === "youtube"
            ? <iframe title="Shared video" className="h-full w-full" src={ytEmbed(videoStage.url)} allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
            : <video className="h-full w-full bg-black" src={asset(videoStage.url)} controls autoPlay playsInline />}
          {!guest && <button data-testid="video-stop" onClick={stopVideo} className="absolute left-3 top-3 z-10 rounded-full bg-destructive px-3 py-1.5 text-xs font-semibold">Stop video</button>}
        </div>
      )}
      {view === "webinar" && attendeeCount > 0 && <span className="absolute right-2 top-2 z-30 rounded-full bg-black/60 px-3 py-1 text-xs" data-testid="attendee-count">{attendeeCount} attendee{attendeeCount > 1 ? "s" : ""} watching</span>}
      {body}
    </div>
  );
}

const VIEWS = [
  ["gallery", "Gallery", SquaresFour], ["speaker", "Speaker", MicrophoneStage],
  ["presentation", "Presentation", Presentation], ["sidebyside", "Side by side", SquareHalf],
  ["spotlight", "Spotlight", Star], ["classroom", "Classroom", Student], ["webinar", "Webinar", Broadcast],
];

function ViewSwitcher({ view, setView }) {
  const [open, setOpen] = useState(false);
  const cur = VIEWS.find((v) => v[0] === view) || VIEWS[0];
  const Icon = cur[2];
  return (
    <div className="relative" data-testid="view-switcher">
      <button data-testid="view-switcher-btn" onClick={() => setOpen((o) => !o)} className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-white/80 hover:border-primary">
        <Icon size={15} weight="duotone" /> {cur[1]} <CaretDown size={12} />
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-[70] w-44 rounded-xl border border-white/10 bg-[#0b1020] p-1 shadow-xl">
          {VIEWS.map(([k, label, I]) => (
            <button key={k} data-testid={`view-${k}`} onClick={() => { setView(k); setOpen(false); }}
              className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs ${view === k ? "bg-primary text-primary-foreground" : "hover:bg-white/5"}`}>
              <I size={15} weight="duotone" /> {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const ASSIGNABLE_ROLES = [["cohost", "Co-host"], ["moderator", "Moderator"], ["presenter", "Presenter"], ["panelist", "Panelist"], ["attendee", "Attendee"]];

function RoleMenu({ r, room }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button data-testid={`role-menu-${r.id}`} onClick={() => setOpen((o) => !o)} className="flex items-center gap-0.5 rounded-md bg-white/10 px-2 py-1 text-[10px]">{ROLE_LABEL[r.role || "panelist"]}<CaretDown size={9} /></button>
      {open && (
        <div className="absolute right-0 z-[70] mt-1 w-40 rounded-xl border border-white/10 bg-[#0b1020] p-1 shadow-xl">
          {ASSIGNABLE_ROLES.map(([k, label]) => (
            <button key={k} data-testid={`set-role-${r.id}-${k}`} onClick={() => { room.setRole(r.id, k); setOpen(false); }}
              className={`flex w-full items-center rounded-lg px-3 py-1.5 text-left text-[11px] ${r.role === k ? "bg-primary text-primary-foreground" : "hover:bg-white/5"}`}>{label}</button>
          ))}
          <div className="my-1 border-t border-white/10" />
          <button data-testid={`spotlight-${r.id}`} onClick={() => { room.spotlight(r.id); setOpen(false); }} className="flex w-full items-center rounded-lg px-3 py-1.5 text-left text-[11px] hover:bg-white/5">Spotlight for all</button>
          {r.canSpeak === false
            ? <button data-testid={`grant-speak-${r.id}`} onClick={() => { room.grantSpeak(r.id, true); setOpen(false); }} className="flex w-full items-center rounded-lg px-3 py-1.5 text-left text-[11px] hover:bg-white/5">Allow to speak</button>
            : <button data-testid={`revoke-speak-${r.id}`} onClick={() => { room.grantSpeak(r.id, false); setOpen(false); }} className="flex w-full items-center rounded-lg px-3 py-1.5 text-left text-[11px] hover:bg-white/5">Revoke speaking</button>}
        </div>
      )}
    </div>
  );
}

function Ctrl({ on, onClick, On, Off, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex h-12 w-12 items-center justify-center rounded-full ${on ? "bg-white/10" : "bg-destructive"}`}>
      {on ? <On size={20} weight="duotone" /> : <Off size={20} weight="duotone" />}
    </button>
  );
}
